import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice, resolveImg } from "@/lib/format";
import { AdminOrders } from "@/components/AdminOrders";
import { Loader2, Plus, Pencil, Trash2, X, Upload, LogOut, ExternalLink } from "lucide-react";

const EMPTY = {
  name: "",
  category: "Salwar Suits",
  price: "",
  description: "",
  material: "",
  images: [],
  sizes: ["XS", "S", "M", "L", "XL"],
  active: true,
  order: 99,
};

const CATEGORIES = ["Salwar Suits", "Co-ord Sets", "Dresses", "Anarkali", "Lehengas", "Accessories"];
const ALL_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function ProductForm({ initial, authHeader, onDone, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSize = (s) =>
    setForm((f) => ({
      ...f,
      sizes: f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s],
    }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      order: parseInt(form.order) || 99,
    };
    try {
      if (initial.id) {
        await api.put(`/admin/products/${initial.id}`, payload, { headers: authHeader() });
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload, { headers: authHeader() });
        toast.success("Product created");
      }
      onDone();
    } catch {
      toast.error("Could not save product");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-aak-fg/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="product-form">
      <div className="bg-aak-bg border border-aak-border w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-aak-border sticky top-0 bg-aak-bg">
          <h3 className="font-serif text-2xl text-aak-fg">{initial.id ? "Edit Product" : "New Product"}</h3>
          <button data-testid="form-close-btn" onClick={onCancel} className="text-aak-mutedfg hover:text-aak-fg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Name">
            <input
              data-testid="form-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="form-in"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select
                data-testid="form-category"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="form-in"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Price (₹)">
              <input
                data-testid="form-price"
                type="number"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                className="form-in"
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              data-testid="form-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              className="form-in resize-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Material">
              <input
                data-testid="form-material"
                value={form.material}
                onChange={(e) => set("material", e.target.value)}
                className="form-in"
              />
            </Field>
            <Field label="Display Order">
              <input
                type="number"
                value={form.order}
                onChange={(e) => set("order", e.target.value)}
                className="form-in"
              />
            </Field>
          </div>

          <Field label="Sizes">
            <div className="flex gap-2 flex-wrap">
              {ALL_SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSize(s)}
                  className={`w-11 h-10 text-xs border ${
                    form.sizes.includes(s)
                      ? "bg-aak-fg text-aak-bg border-aak-fg"
                      : "border-aak-border text-aak-fg"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Images">
            <div className="flex flex-wrap gap-3 mb-3">
              {form.images.map((im, i) => (
                <div key={i} className="relative w-20 h-28 border border-aak-border overflow-hidden group">
                  <img src={resolveImg(im)} alt="" className="w-full h-full object-cover object-top" />
                  <button
                    type="button"
                    data-testid={`remove-image-${i}`}
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 bg-aak-bg/90 text-aak-fg w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" id="upload-input" data-testid="form-upload-input" />
            <label
              htmlFor="upload-input"
              className="inline-flex items-center gap-2 border border-aak-border px-4 py-2 text-xs uppercase tracking-[0.15em] text-aak-fg cursor-pointer hover:border-aak-fg"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Image
            </label>
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-aak-border flex gap-3 justify-end">
          <button onClick={onCancel} className="px-6 py-3 text-xs uppercase tracking-[0.2em] border border-aak-border text-aak-fg hover:border-aak-fg">
            Cancel
          </button>
          <button
            data-testid="form-save-btn"
            onClick={save}
            disabled={saving}
            className="px-6 py-3 text-xs uppercase tracking-[0.2em] bg-aak-fg text-aak-bg hover:bg-aak-primary transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {initial.id ? "Save Changes" : "Create Product"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.22em] text-aak-mutedfg block mb-2">{label}</label>
    {children}
  </div>
);

export default function AdminDashboard() {
  const { admin, authHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | EMPTY | product
  const [view, setView] = useState("products"); // products | orders

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/products", { headers: authHeader() });
      setProducts(data);
    } catch {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (admin && admin.email) load();
  }, [admin, load]);

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${id}`, { headers: authHeader() });
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (admin === null || (admin && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-aak-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative z-10" data-testid="admin-dashboard">
      <header className="border-b border-aak-border bg-aak-bg/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="font-serif text-3xl text-aak-fg leading-none">AAK<span className="text-aak-primary">.</span></p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-aak-mutedfg mt-1">Atelier Admin</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-aak-mutedfg hover:text-aak-fg">
              <ExternalLink className="w-4 h-4" /> View Store
            </a>
            <button
              data-testid="admin-logout-btn"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-aak-mutedfg hover:text-aak-fg"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex gap-2 mb-10">
          <button
            data-testid="tab-products"
            onClick={() => setView("products")}
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
              view === "products" ? "bg-aak-fg text-aak-bg border-aak-fg" : "border-aak-border text-aak-mutedfg hover:border-aak-fg hover:text-aak-fg"
            }`}
          >
            Products
          </button>
          <button
            data-testid="tab-orders"
            onClick={() => setView("orders")}
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
              view === "orders" ? "bg-aak-fg text-aak-bg border-aak-fg" : "border-aak-border text-aak-mutedfg hover:border-aak-fg hover:text-aak-fg"
            }`}
          >
            Orders
          </button>
        </div>

        {view === "orders" ? (
          <AdminOrders authHeader={authHeader} />
        ) : (
          <>
        <div className="flex items-end justify-between mb-10">
          <div>
            <h1 className="font-serif text-4xl md:text-5xl text-aak-fg tracking-tight">Products</h1>
            <p className="text-sm text-aak-mutedfg mt-2">{products.length} pieces in your collection</p>
          </div>
          <button
            data-testid="add-product-btn"
            onClick={() => setEditing({ ...EMPTY })}
            className="flex items-center gap-2 bg-aak-fg text-aak-bg px-6 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-aak-primary transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>

        <div className="border border-aak-border divide-y divide-aak-border bg-aak-surface/40">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-row-${p.id}`} className="flex items-center gap-4 p-4">
              <div className="w-16 h-20 bg-aak-muted overflow-hidden flex-shrink-0">
                {p.images?.[0] && <img src={resolveImg(p.images[0])} alt={p.name} className="w-full h-full object-cover object-top" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-aak-primary">{p.category}</p>
                <h3 className="font-serif text-xl text-aak-fg truncate">{p.name}</h3>
                <p className="text-sm text-aak-mutedfg">{formatPrice(p.price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`edit-${p.id}`}
                  onClick={() => setEditing({ ...EMPTY, ...p })}
                  className="w-10 h-10 flex items-center justify-center border border-aak-border text-aak-fg hover:border-aak-fg"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`delete-${p.id}`}
                  onClick={() => del(p.id, p.name)}
                  className="w-10 h-10 flex items-center justify-center border border-aak-border text-aak-fg hover:border-red-600 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {products.length === 0 && (
            <div className="p-12 text-center text-aak-mutedfg">No products yet. Add your first piece.</div>
          )}
        </div>
          </>
        )}
      </main>

      {editing && (
        <ProductForm
          initial={editing}
          authHeader={authHeader}
          onDone={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}
